<template>
  <div class="editor">
    <div class="container">
      <div class="row mb-3 bg-white">
        <div class="col">
          <editor-menu-bar
            :editor="editor"
            v-slot="{ commands, isActive, getMarkAttrs, menu }"
            class="bg-white pt-2 pl-2"
          >
            <div class="row">
              <div class="col">
                <button
                  class="btn btn-light btn-sm mr-2 mb-2 font-weight-bold"
                  :class="{ 'active': isActive.paragraph() }"
                  @click="commands.paragraph"
                  title="paragraph"
                  v-tooltip.bottom="'Paragraph'"
                >P</button>

                <button
                  class="btn btn-light btn-sm mr-2 mb-2 font-weight-bold"
                  :class="{ 'active': isActive.bold() }"
                  @click="commands.bold"
                  title="bold"
                  v-tooltip.bottom="'Bold'"
                >B</button>

                <button
                  class="btn btn-light btn-sm mr-2 mb-2"
                  :class="{ 'active': isActive.italic() }"
                  @click="commands.italic"
                  title="italic"
                  v-tooltip.bottom="'Italic'"
                >
                  <i class="fas fa-italic"></i>
                </button>

                <button
                  class="btn btn-light btn-sm mr-2 mb-2"
                  :class="{ 'active': isActive.strike() }"
                  @click="commands.strike"
                  title="strike"
                  v-tooltip.bottom="'Strike'"
                >
                  <i class="fas fa-strikethrough"></i>
                </button>
                <button
                  class="btn btn-light btn-sm mr-2 mb-2"
                  :class="{ 'active': isActive.underline() }"
                  @click="commands.underline"
                  title="underline"
                  v-tooltip.bottom="'Underline'"
                >
                  <i class="fas fa-underline"></i>
                </button>

                <button
                  class="btn btn-light btn-sm mr-2 mb-2"
                  :class="{ 'active': isActive.code() }"
                  @click="commands.code"
                  title="code"
                  v-tooltip.bottom="'Code'"
                >
                  <i class="fas fa-code"></i>
                </button>

                <button
                  class="btn btn-light btn-sm mr-2 mb-2 font-weight-bold"
                  :class="{ 'active': isActive.heading({level: 1}) }"
                  @click="commands.heading({level: 1})"
                  title="heading"
                  v-tooltip.bottom="'Heading 1'"
                >H1</button>

                <button
                  class="btn btn-light btn-sm mr-2 mb-2 font-weight-bold"
                  :class="{ 'active': isActive.heading({level: 2}) }"
                  @click="commands.heading({level: 2})"
                  title="heading"
                  v-tooltip.bottom="'Heading 2'"
                >H2</button>

                <button
                  class="btn btn-light btn-sm mr-2 mb-2 font-weight-bold"
                  :class="{ 'active': isActive.heading({level: 3}) }"
                  @click="commands.heading({level: 3})"
                  title="heading"
                  v-tooltip.bottom="'Heading 3'"
                >H3</button>

                <button
                  class="btn btn-light btn-sm mr-2 mb-2"
                  :class="{ 'active': isActive.bullet_list() }"
                  @click="commands.bullet_list"
                  title="bullet_list"
                  v-tooltip.bottom="'Bullet Points'"
                >
                  <i class="fas fa-list"></i>
                </button>

                <button
                  class="btn btn-light btn-sm mr-2 mb-2"
                  :class="{ 'active': isActive.ordered_list() }"
                  @click="commands.ordered_list"
                  title="ordered_list"
                  v-tooltip.bottom="'Numbered List'"
                >
                  <i class="fas fa-list-ol"></i>
                </button>

                <button
                  class="btn btn-light btn-sm mr-2 mb-2"
                  :class="{ 'active': isActive.blockquote() }"
                  @click="commands.blockquote"
                  title="blockquote"
                  v-tooltip.bottom="'Blockquote'"
                >
                  <i class="fas fa-quote-right"></i>
                </button>

                <button
                  class="btn btn-light btn-sm mr-2 mb-2"
                  :class="{ 'active': isActive.code_block() }"
                  @click="commands.code_block"
                  title="code_block"
                  v-tooltip.bottom="'Highlight Block'"
                >[ ]</button>

                <button class="btn btn-light btn-sm mr-2 mb-2" @click="commands.horizontal_rule">HR</button>

                <button
                  class="btn btn-light btn-sm mr-2 mb-2"
                  :class="{ 'active': isActive.image() }"
                  @click="showImageSelectorModal(commands.image)"
                  title="image"
                  v-tooltip.bottom="'Insert Image'"
                >
                  <i class="fas fa-image"></i>
                </button>

                <button
                  class="btn btn-light btn-sm mr-2 mb-2"
                  :class="{ 'active': isActive.pdfdownload() }"
                  @click="showPdfSelectorModal(commands.pdfdownload)"
                  title="image"
                  v-tooltip.bottom="'PDF Download'"
                >
                  <i class="fas fa-file-alt"></i>
                </button>

                <button
                  class="btn btn-light btn-sm mb-2"
                  :class="{ 'active': isActive.table() }"
                  @click="commands.createTable({rowsCount: 3, colsCount: 3, withHeaderRow: true })"
                  v-tooltip.bottom="'Table'"
                >
                  <i class="fas fa-table"></i>
                </button>

                <div class="position-relative">
                  <span
                    v-if="isActive.table()"
                    class="bg-secondary p-2 position-absolute"
                    style="right: 12rem; z-index:10"
                  >
                    <div class="row mb-2">
                      <div class="col">
                        <h6>Table</h6>
                        <button
                          class="btn btn-light btn-sm mr-2"
                          @click="commands.deleteTable"
                          v-tooltip.bottom="'Delete Table'"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>

                    <div class="row mb-2">
                      <div class="col">
                        <h6>Columns</h6>
                        <button
                          class="btn btn-light btn-sm mr-2"
                          @click="commands.addColumnBefore"
                          v-tooltip.bottom="'Add Column Before'"
                        >
                          <i class="fas fa-arrow-left"></i>
                        </button>
                        <button
                          class="btn btn-light btn-sm mr-2"
                          @click="commands.addColumnAfter"
                          v-tooltip.bottom="'Add Column After'"
                        >
                          <i class="fas fa-arrow-right"></i>
                        </button>
                        <button
                          class="btn btn-light btn-sm mr-2"
                          @click="commands.deleteColumn"
                          v-tooltip.bottom="'Delete Column'"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>

                    <div class="row mb-2">
                      <div class="col">
                        <h6>Rows</h6>
                        <button
                          class="btn btn-light btn-sm mr-2"
                          @click="commands.addRowBefore"
                          v-tooltip.bottom="'Add Row Before'"
                        >
                          <i class="fas fa-arrow-up"></i>
                        </button>
                        <button
                          class="btn btn-light btn-sm mr-2"
                          @click="commands.addRowAfter"
                          v-tooltip.bottom="'Add Row After'"
                        >
                          <i class="fas fa-arrow-down"></i>
                        </button>
                        <button
                          class="btn btn-light btn-sm mr-2"
                          @click="commands.deleteRow"
                          v-tooltip.bottom="'Delete Row'"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col">
                        <button
                          class="btn btn-light btn-sm mr-2"
                          @click="commands.toggleCellMerge"
                          v-tooltip.bottom="'Toggle Cell Merge'"
                        >
                          <i class="far fa-code-merge"></i>
                        </button>
                      </div>
                    </div>
                  </span>
                </div>
              </div>
              <div class="col-auto ml-auto">
                <button
                  class="btn btn-light btn-sm mr-2 mb-2"
                  @click="commands.undo"
                  v-tooltip.bottom="'Undo'"
                >
                  <i class="fas fa-undo"></i>
                </button>

                <button
                  class="btn btn-light btn-sm mr-2 mb-2"
                  @click="commands.redo"
                  v-tooltip.bottom="'Redo'"
                >
                  <i class="fas fa-redo"></i>
                </button>
              </div>
            </div>
          </editor-menu-bar>
        </div>
      </div>

      <editor-menu-bubble
        :editor="editor"
        @hide="hideBubbleMenu"
        :keep-in-bounds="keepInBounds"
        v-slot="{ commands, isActive, getMarkAttrs, menu }"
      >
        <div
          class="menububble"
          :class="{ 'is-active': menu.isActive }"
          :style="`left: ${menu.left + 150}px; bottom: ${menu.bottom}px;`"
        >
          <div class="container">
            <div class="row">
              <div class="col">
                <!--  -->

                <form
                  class="menububble__form"
                  v-if="linkMenuIsActive"
                  @submit.prevent="setLinkUrl(commands.link, linkUrl, linkAlt, linkTitle)"
                >
                  <div class="row">
                    <div class="col my-auto">
                      <div class="row">
                        <div class="col">
                          <input
                            class="menububble__input"
                            type="text"
                            v-model="linkUrl"
                            placeholder="https:// or #example"
                            ref="linkInput"
                            @keydown.esc="hideLinkMenu"
                          />
                        </div>
                      </div>

                      <div class="row">
                        <div class="col">
                          <input
                            class="menububble__input"
                            type="text"
                            v-model="linkAlt"
                            placeholder="Link ALT"
                            ref="linkAltInput"
                            @keydown.esc="hideLinkMenu"
                          />
                        </div>
                      </div>
                      <div class="row">
                        <div class="col">
                          <input
                            class="menububble__input"
                            type="text"
                            v-model="linkTitle"
                            placeholder="Link Title"
                            ref="linkTitleInput"
                            @keydown.esc="hideLinkMenu"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="col-auto my-auto ml-auto">
                      <button
                        class="menububble__button"
                        @click="setLinkUrl(commands.link, null, null, null)"
                        type="button"
                      >
                        <i class="fas fa-trash"></i>
                      </button>

                      <button
                        class="menububble__button"
                        @click="setLinkUrl(commands.link, linkUrl, linkAlt, linkTitle)"
                        type="submit"
                      >
                        <i class="fas fa-check"></i>
                      </button>
                    </div>
                  </div>
                </form>

                <form
                  class="menububble__form"
                  v-if="idMenuIsActive"
                  @submit.prevent="setIdText(commands.textWithId, idText)"
                >
                  <div class="row">
                    <div class="col my-auto">
                      <input
                        class="menububble__input"
                        type="text"
                        v-model="idText"
                        placeholder="example-id"
                        ref="idInput"
                        @keydown.esc="hideIdMenu"
                      />
                    </div>

                    <div class="col-auto my-auto ml-auto">
                      <button
                        class="menububble__button"
                        @click="setIdText(commands.textWithId, null)"
                        type="button"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </form>

                <template v-if="!linkMenuIsActive && !idMenuIsActive">
                  <button
                    class="menububble__button"
                    :class="{ 'is-active': isActive.bold() }"
                    @click="commands.bold"
                  >
                    <i class="fas fa-bold"></i>
                  </button>

                  <button
                    class="menububble__button"
                    :class="{ 'is-active': isActive.italic() }"
                    @click="commands.italic"
                  >
                    <i class="fas fa-italic"></i>
                  </button>

                  <button
                    class="menububble__button"
                    :class="{ 'is-active': isActive.code() }"
                    @click="commands.code"
                  >
                    <i class="fas fa-code"></i>
                  </button>

                  <button
                    class="menububble__button"
                    @click="showIdMenu(getMarkAttrs('textWithId'))"
                    :class="{ 'is-active': isActive.textWithId() }"
                  >
                    <i class="fas fa-hashtag"></i>
                  </button>

                  <button
                    class="menububble__button"
                    @click="showLinkMenu(getMarkAttrs('link'))"
                    :class="{ 'is-active': isActive.link() }"
                  >
                    <!-- <span class="mr-2">{{ isActive.link() ? 'Update' : 'Add'}}</span> -->
                    <i class="fas fa-link"></i>
                  </button>
                </template>
              </div>
            </div>
          </div>
        </div>
      </editor-menu-bubble>

      <div class="row mb-3">
        <div class="col bg-light p-3">
          <editor-floating-menu
            :editor="editor"
            v-slot="{ commands, isActive, getMarkAttrs, menu }"
            class="bg-white pt-2 pl-2"
          >
            <div
              :class="`position-absolute shadow ${menu.isActive ? 'opacity-1' : 'opacity-0'}`"
              :style="`top: ${menu.top + 70}px; z-index: 10; left: 6rem`"
            >
              <div class="row">
                <div class="col">
                  <button
                    class="btn btn-light btn-sm mr-2 mb-2 font-weight-bold"
                    :class="{ 'active': isActive.paragraph() }"
                    @click="commands.paragraph"
                    title="paragraph"
                  >P</button>

                  <button
                    class="btn btn-light btn-sm mr-2 mb-2 font-weight-bold"
                    :class="{ 'active': isActive.bold() }"
                    @click="commands.bold"
                    title="bold"
                  >B</button>

                  <button
                    class="btn btn-light btn-sm mr-2 mb-2 font-weight-bold"
                    :class="{ 'active': isActive.heading({level: 1}) }"
                    @click="commands.heading({level: 1})"
                    title="heading"
                  >H1</button>

                  <button
                    class="btn btn-light btn-sm mr-2 mb-2 font-weight-bold"
                    :class="{ 'active': isActive.heading({level: 2}) }"
                    @click="commands.heading({level: 2})"
                    title="heading"
                  >H2</button>

                  <button
                    class="btn btn-light btn-sm mr-2 mb-2 font-weight-bold"
                    :class="{ 'active': isActive.heading({level: 3}) }"
                    @click="commands.heading({level: 3})"
                    title="heading"
                  >H3</button>

                  <button
                    class="btn btn-light btn-sm mr-2 mb-2"
                    :class="{ 'active': isActive.bullet_list() }"
                    @click="commands.bullet_list"
                    title="bullet_list"
                  >
                    <i class="fas fa-list"></i>
                  </button>

                  <button
                    class="btn btn-light btn-sm mr-2 mb-2"
                    :class="{ 'active': isActive.ordered_list() }"
                    @click="commands.ordered_list"
                    title="ordered_list"
                  >
                    <i class="fas fa-list-ol"></i>
                  </button>

                  <button
                    class="btn btn-light btn-sm mr-2 mb-2"
                    :class="{ 'active': isActive.blockquote() }"
                    @click="commands.blockquote"
                    title="blockquote"
                  >
                    <i class="fas fa-quote-right"></i>
                  </button>

                  <button
                    class="btn btn-light btn-sm mr-2 mb-2"
                    :class="{ 'active': isActive.image() }"
                    @click="showImageSelectorModal(commands.image)"
                    title="image"
                  >
                    <i class="fas fa-image"></i>
                  </button>
                </div>
              </div>
            </div>
          </editor-floating-menu>
          <editor-content class="editor__content bg-white p-3" :editor="editor"></editor-content>
        </div>
      </div>

      <image-selector
        :files-url="filesUrl"
        v-if="showImageSelector"
        @selected="insertImage"
        @close="showImageSelector = false"
      ></image-selector>

      <pdf-selector
        :files-url="filesUrl"
        v-if="showImageSelector"
        @selected="insertImage"
        @close="showImageSelector = false"
      ></pdf-selector>
    </div>
  </div>
</template>

<script>
import {
  Editor,
  EditorContent,
  EditorMenuBubble,
  EditorFloatingMenu,
  EditorMenuBar
} from "tiptap";
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Code,
  Italic,
  Strike,
  Underline,
  History,
  Placeholder,
  Table,
  TableHeader,
  TableCell,
  TableRow
} from "tiptap-extensions";
import TextWithId from "./marks/TextWithId";
import Link from "./marks/Link";
import Image from "./nodes/Image";
import PdfDownload from "./nodes/PdfDownload";
// import Ids from "./Ids";
import ImageSelector from "./nodes/ImageSelector";
import PdfSelector from "./nodes/PdfSelector";
import { VTooltip } from "v-tooltip";

export default {
  props: ["value", "filesUrl"],
  directives: {
    tooltip: VTooltip
  },
  data() {
    return {
      editor: new Editor({
        onUpdate: ({ getHTML }) => {
          // this.$emit("update", getHTML());
          this.$emit("input", getHTML());
        },
        extensions: [
          new Blockquote(),
          new BulletList(),
          new CodeBlock(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3] }),
          new HorizontalRule(),
          new ListItem(),
          new OrderedList(),
          new TodoItem(),
          new TodoList(),
          new Link({
            openOnClick: false
          }),
          new TextWithId(),
          new Bold(),
          new Code(),
          new Italic(),
          new Strike(),
          new Underline(),
          new History(),
          new Image(),
          new PdfDownload(),
          new Placeholder({
            emptyNodeClass: "is-empty",
            emptyNodeText: "Write something …",
            showOnlyWhenEditable: true
          }),
          new Table({
            resizable: true
          }),
          new TableHeader(),
          new TableCell(),
          new TableRow()
        ],
        content: this.value
      }),
      linkUrl: null,
      linkAlt: null,
      linkTitle: null,
      linkMenuIsActive: false,
      keepInBounds: true,
      showImageSelector: null,
      imageCommand: null,
      idText: null,
      idTag: null,
      idMenuIsActive: false,

      //

      pdfCommand: null,
      showPdfSelector: false
    };
  },
  methods: {
    insertImage(data) {
      if (data.url != null) {
        this.imageCommand({
          src: data.url,
          classes: "somenewone",
          alt: data.alt,
          title: data.title
        });
        this.showImageSelector = false;
      }
    },
    showImageSelectorModal(command) {
      this.imageCommand = command;
      this.showImageSelector = true;
    },
    showPdfSelectorModal(command) {
      this.pdfCommand = command;
      this.showImageSelector = true;
    },
    showLinkMenu(attrs) {
      this.linkUrl = attrs.href;
      this.linkAlt = attrs.alt;
      this.linkTitle = attrs.title;
      this.linkMenuIsActive = true;
      this.$nextTick(() => {
        this.$refs.linkInput.focus();
      });
    },
    hideBubbleMenu() {
      this.hideLinkMenu();
      this.hideIdMenu();
    },
    hideLinkMenu() {
      this.linkUrl = null;
      this.linkAlt = null;
      this.linkTitle = null;
      this.linkMenuIsActive = false;
    },
    showIdMenu(attrs) {
      this.idTag = attrs.element_tag;
      this.idText = attrs.id;
      this.idMenuIsActive = true;
      this.$nextTick(() => {
        this.$refs.idInput.focus();
      });
    },
    hideIdMenu() {
      this.idText = null;
      this.idTag = null;
      this.idMenuIsActive = false;
    },
    setLinkUrl(command, url, alt, title) {
      console.log(command);
      command({ href: url, alt: alt, title: title });
      this.hideLinkMenu();
    },
    setIdText(command, text) {
      console.log(text);
      command({ id: text, element_tag: this.idTag });
      this.hideIdMenu();
    }
  },
  mounted() {
    // $(window).scroll(function(e) {
    //   var $el = $(".fixedElement");
    //   var isPositionFixed = $el.css("position") == "fixed";
    //   if ($(this).scrollTop() > 200 && !isPositionFixed) {
    //     $el.css({ position: "fixed", top: "2rem" });
    //   }
    //   if ($(this).scrollTop() < 200 && isPositionFixed) {
    //     $el.css({ position: "static", top: "0px" });
    //   }
    // });
  },
  beforeDestroy() {
    this.editor.destroy();
  },
  components: {
    EditorContent,
    EditorMenuBubble,
    ImageSelector,
    EditorFloatingMenu,
    EditorMenuBar,
    PdfSelector
  }
};
</script>

<style lang="scss" scoped>
.vertical-divider {
  border-right: 1px #333 solid;
}
p.is-empty:first-child::before {
  content: attr(data-empty-text) !important;
  float: left !important;
  color: black !important;
  pointer-events: none !important;
  height: 0 !important;
}
</style>
<style lang="scss" scoped>
@import "../../../sass/bootstrap.scss";
</style>
<style lang="scss">
@import "../../../sass/styles.scss";
img::-moz-selection {
  /* Code for Firefox */
  color: red;
  background: yellow;
}

img::selection {
  color: red;
  background: yellow;
}
</style>